// English copy for the pricing from 1 November 2026: /pricing-2026 (`pricing`), the "what's
// changing" page /pricing-changes-2026 (`changes`) and what they share (`common`, `app`).
// English is the source of truth: its shape is the PricingCopy type that copy.es.ts and
// copy.pt.ts must match. Numbers live in plans.ts / countries.ts; "{name}" placeholders are filled
// in by the pages (see fill() in ./index.ts). Plan names are product names and stay in English.

/** A plan feature on a pricing card: plain text, or text with a short note under it. */
export type PlanFeature = string | { text: string; note: string };

const en = {
  common: {
    perMonth: "/month",
    /** Link to /on-demand-ftp under the FTP answers and the FTP changes. */
    onDemandFtpLink: "How on-demand FTP works",
    aMonth: "a month",
    today: "Today",
    fromChangeDate: "From 1 November",
    changeDate: "1 November 2026",
    /** "{n}" is a number. */
    gb: "{n} GB",
    tb: "{n} TB",
    mostPopular: "Most popular",
    /** A plan that keeps going under a new name: "Enterprise (now called Max)". */
    renamedPlan: "{from} (now called {to})",
    /** What's included on each plan's card: plain text, or text with a short note under it. */
    planFeatures: {
      free: [
        {
          text: "Your own branding, “Powered by Photohawk”",
          note: "Your logo and colours on every gallery. A small “Powered by Photohawk” badge sits at the foot of your galleries and of your workspace's sign-in page. Pro and Max have no badge.",
        },
        "Team of up to 3 people",
        "Help centre support",
        {
          text: "On-demand FTP server",
          note: "If FTP is essential, start your own from the web uploader, at your own Photohawk address that never changes. It switches off automatically after 1 hour without activity.",
        },
        {
          text: "Previews first uploads",
          note: "The gallery goes live on light previews in minutes while the originals follow, for sales and free downloads alike.",
        },
      ],
      pro: [
        {
          text: "Your own branding",
          note: "Your logo and colours, with no Photohawk badge.",
        },
        "Custom domain",
        "Team of up to 10 people",
        "Visitor analytics",
        {
          text: "On-demand FTP server",
          note: "If FTP is essential, start your own from the web uploader, at your own Photohawk address that never changes. It switches off automatically after 1 hour without activity.",
        },
        {
          text: "Previews first uploads",
          note: "The gallery goes live on light previews in minutes while the originals follow, for sales and free downloads alike.",
        },
        "Email support",
      ],
      max: [
        "Everything in Pro",
        "Video upload",
        "Revenue splitting",
        "Multiple brands",
        "Unlimited team members and roles",
        "Visitor analytics",
        {
          text: "On-demand FTP server",
          note: "If FTP is essential, start your own from the web uploader, at your own Photohawk address that never changes. It switches off automatically after 1 hour without activity.",
        },
        {
          text: "Previews first uploads",
          note: "The gallery goes live on light previews in minutes while the originals follow, for sales and free downloads alike.",
        },
        "Priority support",
      ],
    } as Record<"free" | "pro" | "max", PlanFeature[]>,
    planTaglines: {
      free: "For getting started and occasional events.",
      pro: "For workspaces selling regularly under their own brand.",
      max: "For studios, teams and high volume: the lowest rate, the most storage, video and priority support.",
    },
    countries: {
      uk: "United Kingdom",
      my: "Malaysia",
      ph: "Philippines",
      mx: "Mexico",
      co: "Colombia",
      eur: "Portugal and the eurozone",
      us: "United States",
      ca: "Canada",
    },
    /** For sentences: "The average order {inCountry}". */
    inCountry: {
      uk: "in the United Kingdom",
      my: "in Malaysia",
      ph: "in the Philippines",
      mx: "in Mexico",
      co: "in Colombia",
      eur: "in Portugal and the eurozone",
      us: "in the United States",
      ca: "in Canada",
    },
  },

  pricing: {
    seo: {
      title: "Pricing | Photohawk",
      description:
        "Lower rates on every plan for event photographers: 16% on Lite, 14% on Pro and 12% on Max. Storage and free downloads included.",
    },
    notice: "New pricing from 1 November 2026.",
    noticeLink: "See what's changing",
    hero: {
      badge: "Pricing",
      title: "Lower rates on every plan.",
      /** "{rate}" is the lowest rate. */
      titleHighlight: "As low as {rate}.",
      subtitle:
        "Sell at 16% on Lite, 14% on Pro or 12% on Max. Transaction fees work just as they do today.",
      /** Under the subtitle: the kinds of events the plans are for. */
      events: "The same plans for every kind of event: races and rides, matches, shows, festivals, graduations and conferences.",
    },
    rate: {
      heading: "Your commission on each plan",
      planLabel: "on {plan}",
      points: [
        "Card fees are separate and work just as they do today.",
        "Our commission is the only Photohawk fee on a sale.",
        "Existing custom deals carry over until they end.",
      ],
    },
    plans: {
      heading: "Choose your plan",
      subtitle:
        "Monthly plans in US dollars, with no contract. Each step up lowers your rate and adds storage and free downloads.",
      sellingLabel: "Selling",
      sellingLine: "{rate} commission on sales",
      sellingNote: "Plus transaction fees, as today",
      freeDownloadsLabel: "Free downloads",
      freeDownloadsLine: "{count} photos a month included",
      overageLine: "then $8 per 1,000 photos",
      storageLabel: "Storage",
      storageLine: "{storage} included",
      storageNote: "then packs from $5 a month",
      includedLabel: "Included",
      cta: "Get started",
      ctaFor: "with {plan}",
      planChange:
        "Changing plan: a plan's lower rate applies from your first full month on it. Downgrades take effect at the end of the month.",
    },
    standard: {
      heading: "Standard on every plan",
      subtitle: "Every plan gets these, including Lite at $0 a month.",
      items: [
        { id: "selfie", title: "Selfie search", body: "Buyers take a selfie and see every photo they're in." },
        { id: "bib", title: "Bib and time search", body: "Find photos by race number or by when they were taken." },
        { id: "preview", title: "Preview protection", body: "Screenshot protection with tap-to-reveal, so previews sell instead of getting copied." },
        {
          id: "uploader",
          title: "Web uploader and Photohawk Studio",
          body: "The standard way to upload: it sends many photos at once, adapts to your connection and resumes by itself. On every plan, light previews can go up first so your gallery is live in minutes. Studio runs in your web browser today; the Photohawk Studio app for Mac, iPhone and iPad launches in November 2026.",
        },
        { id: "whatsapp", title: "WhatsApp delivery, a Photohawk service for buyers", body: "Buyers can choose to pay a little extra at checkout to get their photos on WhatsApp, ready to share. Photohawk runs it on every gallery: there's nothing for you to set up, price or switch off." },
      ],
    },
    storage: {
      heading: "Storage",
      subtitle:
        "Every plan includes storage. Store more and, with a card on file, packs are added automatically: you're billed for the cheapest mix that covers what you store. No card on file? New uploads pause until you add one; nothing is deleted and your galleries keep selling.",
      includedHeading: "Included",
      packsHeading: "Extra storage packs",
      packPrice: "${price} a month",
      examplesHeading: "How packs add up",
      /** "{plan}", "{stored}", "{over}", "{mix}", "{price}" are filled in. */
      example: "{plan} storing {stored}: {over} over, billed as {mix} = ${price} a month",
      note: "We'll email you when you reach 80% of your storage. Packs come off when you free up space.",
    },
    freeDownloads: {
      heading: "Free-download galleries",
      subtitle:
        "Share galleries your buyers download for free. Each plan includes photos every month; after that it's $8 per 1,000 photos ($0.008 a photo) on every plan.",
      line: "{count} photos a month",
      note: "The allowance starts again each month, and galleries keep working above it.",
    },
    example: {
      heading: "What you keep",
      subtitle: "A UK order paid by card, on your own Stripe account, on each plan. Try a different amount.",
      orderLabel: "Order value",
      order: "Order",
      cardFee: "Stripe card fee",
      cardFeeNote: "Charged by Stripe to your account, e.g. UK cards 1.5% + 20p",
      colPlan: "Plan",
      colCommission: "Photohawk commission",
      colKeep: "You keep about",
      planCell: "{plan} ({rate})",
      note: "Stripe's fee depends on the card: international cards and currency conversion usually cost more. Our commission is the only Photohawk fee.",
    },
    cardFees: {
      heading: "How card fees work",
      intro: "Card and payment processing fees work just as they do today, separately from our commission.",
      items: [
        {
          id: "stripe",
          title: "On your own Stripe account",
          body: "In the UK, the EU, the US and other Stripe countries, Stripe charges its standard processing fee directly to your Stripe account.",
        },
        {
          id: "managed",
          title: "Where Photohawk processes the payment for you",
          body: "In Malaysia, the Philippines, Mexico, Colombia, Ecuador and other countries where buyers pay with local wallets, a payment processing fee is deducted at cost-based local rates, as it is today.",
        },
      ],
      linkLabel: "See card fees by country",
    },
    /** "Compare every feature": the full table (rows and cells in plans.ts COMPARE_SECTIONS). */
    compare: {
      heading: "Compare every feature",
      subtitle: "Everything in Photohawk Studio, plan by plan. Studio works in your web browser on every plan today.",
      featureColumn: "Feature",
      included: "Included",
      notIncluded: "Not included",
      /** Link to the table from the changes page and the Studio page. */
      linkLabel: "Compare every feature",
      sections: {
        selling: "Selling",
        finding: "Finding photos",
        galleries: "Galleries",
        brand: "Your brand",
        insights: "Insights",
        team: "Team",
        uploading: "Uploading",
        storage: "Storage",
        support: "Support",
      },
      rows: {
        commission: { label: "Commission on sales", note: "Our only fee on a sale. Card fees work just as they do today." },
        products: { label: "Digital downloads, prints and products", note: "Your own products and prices." },
        packages: { label: "Packages and bundles", note: "" },
        coupons: { label: "Coupons", note: "" },
        preorder: { label: "Pre-order pricing", note: "" },
        whatsapp: { label: "WhatsApp delivery (a Photohawk service for buyers)", note: "An optional extra buyers pay for at checkout, so their photos arrive on WhatsApp ready to share. Run by Photohawk on every gallery; not a setting you control." },
        payouts: { label: "Payouts", note: "To your own Stripe account, or paid out by Photohawk where we process the payment for you." },
        revenueSplit: { label: "Revenue splitting", note: "Share a gallery's sales with photographers, crews, clubs or sponsors. Photohawk works out each share and pays them for you." },
        selfie: { label: "Selfie search", note: "" },
        bib: { label: "Bib and text search", note: "Find photos by race number or by text in the photo." },
        time: { label: "Time search", note: "" },
        find: { label: "Listed in Find your photos", note: "Buyers can find your public galleries on photohawk.com." },
        profiles: { label: "Saved searches", note: "Save a selfie, a number or text and see new matches." },
        faceData: { label: "Face data for selfie search", note: "No expiry by default, or a period you choose, from 1 November 2026." },
        galleries: { label: "Galleries and collections", note: "As many as you need." },
        private: { label: "Private galleries and passwords", note: "" },
        protection: { label: "Preview protection", note: "Screenshot protection with tap-to-reveal." },
        watermarks: { label: "Watermarks", note: "" },
        freeDownloads: { label: "Free-download photos included", note: "The allowance starts again each month." },
        freeOverage: { label: "Free-download photos above the allowance", note: "Galleries keep working." },
        video: { label: "Video upload", note: "" },
        address: { label: "Your Photohawk address", note: "yourname.photohawk.com" },
        customDomain: { label: "Custom domain", note: "Your galleries at your own web address." },
        pages: { label: "Pages", note: "Your own pages alongside your galleries." },
        emails: { label: "Custom email templates", note: "Edit the emails buyers get from each gallery." },
        logo: { label: "Your logo and colours", note: "On your galleries, pages and emails." },
        noBadge: { label: "No “Powered by Photohawk” badge", note: "On Lite, a small badge sits at the foot of your galleries and your sign-in page." },
        live: { label: "Live visitors", note: "How many people are viewing a gallery right now." },
        analytics: { label: "Visitor analytics", note: "Users and visits over time, gallery by gallery." },
        sales: { label: "Orders and sales over time", note: "" },
        collectionDownloads: { label: "Downloads by collection", note: "" },
        people: { label: "People in your workspace", note: "Gallery collaborators count too. People already in your workspace keep their access." },
        roles: { label: "Roles", note: "Owner, administrator, upload-only and sales-only access." },
        collaborators: { label: "Gallery collaborators", note: "" },
        webUploader: { label: "Web uploader", note: "Sends many photos at once and resumes by itself." },
        previewsFirst: { label: "Previews first", note: "Galleries go live on light previews in minutes while the originals follow." },
        soldFirst: { label: "Sold photos first", note: "A photo bought or downloaded before its original is up goes next." },
        originalsBy: { label: "“Originals by” date for buyers", note: "Tell buyers when the originals will be ready." },
        ftp: { label: "On-demand FTP server", note: "If FTP is essential: your own server, started from the web uploader. It switches off automatically after 1 hour without activity." },
        cloudImport: { label: "Import from Dropbox and Google Drive", note: "" },
        studioApp: { label: "Photohawk Studio app for Mac, iPhone and iPad", note: "Studio works in your web browser today." },
        included: { label: "Storage included", note: "" },
        packs: { label: "Extra storage", note: "Added automatically with a card on file, billed as the cheapest mix." },
        you: { label: "Support for your workspace", note: "" },
        buyers: { label: "Photohawk answers your buyers", note: "Buyers' questions come to Photohawk support, unless you set your own address." },
      },
      /** Cell text: "{rate}" a percentage, "{storage}" GB / TB, "{n}" a number, "{usd}" US dollars. */
      values: {
        rate: "{rate}",
        storage: "{storage}",
        perMonth: "{n} a month",
        upToPeople: "Up to {n}",
        unlimited: "Unlimited",
        upToDomains: "Up to {n}",
        months: "{n} months",
        fromNovember: "From November 2026",
        packs: "From ${usd} per {gb} GB a month",
        overage: "${usd} per 1,000",
        supportHelp: "Help centre",
        supportEmail: "Email",
        supportPriority: "Priority",
      },
    },
    faq: {
      heading: "Questions",
      items: [
        {
          q: "Are card fees included?",
          a: "No. Card fees are charged by the payment provider just as they are today: by Stripe to your own account, or deducted at cost-based local rates where Photohawk processes the payment for you. Our commission (16% on Lite, 14% on Pro, 12% on Max) is the only Photohawk fee.",
        },
        {
          q: "Is selfie search included?",
          a: "Yes, on every plan, including Lite. So are bib and time search, preview protection and the web uploader. The Photohawk Studio app for Mac, iPhone and iPad comes to every plan when it launches in November 2026.",
        },
        {
          q: "Can I change plan any time?",
          a: "Yes. Plans are monthly, with no annual contract. When you move to a plan with a lower rate, that rate applies from your first full month on it. Downgrades take effect at the end of the month.",
        },
        {
          q: "I have a custom deal. Does it change?",
          a: "No. Existing custom deals carry over until they end.",
        },
        {
          q: "What happens to free downloads above the allowance?",
          a: "Your free-download galleries keep working. Each month your plan includes 1,000 photos on Lite, 5,000 on Pro and 10,000 on Max. After that it's $8 per 1,000 photos ($0.008 a photo) on every plan.",
        },
        {
          q: "What if I need more storage?",
          a: "Lite includes 150 GB, Pro 1 TB and Max 3 TB. Above that, with a card on file, packs are added automatically: +100 GB for $5, +1 TB for $25 or +5 TB for $100 a month, billed as the cheapest mix that covers what you store. Without a card on file, new uploads pause until you add one: nothing is deleted and your galleries keep selling.",
        },
        {
          q: "What do I get on Max?",
          a: "The lowest rate, 12%, plus 3 TB of storage, 10,000 free-download photos a month, video upload, unlimited team members, visitor analytics and priority support.",
        },
        {
          q: "How many people can be in my workspace?",
          a: "Up to 3 people on Lite, up to 10 on Pro and as many as you like on Max. Already have more people in your workspace? They keep their access: the limit only applies when you invite someone new.",
        },
        {
          id: "ftp",
          q: "Can I still upload by FTP?",
          a: "Yes, if FTP is essential. The web uploader is now the standard way to upload, on every plan: it sends many photos at once, far more than an FTP app like FileZilla (2 at a time by default, and no more than 10), and resumes by itself if the connection drops. If you still need FTP, everyone in your workspace, on every plan, can start their own on-demand FTP server from the web uploader, at a personal Photohawk address that never changes; it switches off automatically after 1 hour without activity. Shared FTP servers close on 1 November 2026.",
        },
        {
          q: "Is Photohawk Friends & Family part of a plan?",
          a: "No. Friends & Family, our app for your family's photos, is a separate app, coming soon, and isn't part of any Studio plan. Running your galleries and uploads? That's Photohawk Studio: it works in your web browser on every plan today, and the Studio app for Mac, iPhone and iPad launches in November 2026.",
        },
      ],
    },
  },

  changes: {
    seo: {
      title: "Pricing is changing on 1 November 2026 | Photohawk",
      description:
        "From 1 November 2026, commission comes down on every Photohawk plan, Pro and Max get more storage, and free-download photos are included each month.",
    },
    hero: {
      badge: "Pricing update",
      title: "Pricing is changing on 1 November 2026.",
      titleHighlight: "Lower commission on every plan.",
      subtitle:
        "Every plan's rate comes down, Pro and Max get more storage, and free-download photos are now included each month. You don't need to do anything.",
      ctaPrimary: "See the new pricing",
      ctaSecondary: "Compare your plan",
    },
    why: {
      heading: "What gets better",
      items: [
        {
          id: "rates",
          title: "Lower commission on every plan",
          body: "Every plan's rate drops, by 1.5 to 9 points. Pay as you go and Lite become the new Lite, with no monthly fee and 16%, Pro goes to 14%, and Enterprise, now called Max, goes to 12%.",
        },
        {
          id: "storage",
          title: "More storage on every plan",
          body: "Lite goes from 100 GB to 150 GB, Pro from 500 GB to 1 TB and Max from 1 TB to 3 TB. Pay as you go's 25 GB becomes 150 GB on Lite.",
        },
        {
          id: "downloads",
          title: "Free-download photos included",
          body: "Today every photo in a free-download gallery is charged. From 1 November, 1,000 to 10,000 a month are included, then it's $8 per 1,000: the same as or less than today's rate on every plan.",
        },
        {
          id: "simple",
          title: "Simpler pricing",
          body: "Three plans instead of four, one commission per plan, and extra storage in simple packs.",
        },
        {
          id: "order",
          title: "Transaction fees unchanged",
          body: "Transaction fees work just as they do today, separately from our commission.",
        },
        {
          id: "deals",
          title: "Your deal is honoured",
          body: "If you have a custom deal, it carries over until it ends.",
        },
      ],
      honestHeading: "The one price that goes up",
      // The break-even is computed: $4.01 more a month ÷ 1.5 points lower commission ≈ $267 of sales.
      honestBody:
        "Enterprise, now called Max, is $49 a month (was $44.99) from your first renewal on or after 1 November. For that it includes 3 TB instead of 1 TB, 10,000 free-download photos a month and the lowest rate, 12% instead of 13.5%: once you sell about $270 a month, the lower rate more than covers the difference. Pro comes down from $29.99 to $29.",
    },
    when: {
      heading: "What happens on 1 November",
      subtitle: "One date for everyone. Your workspace moves to its new plan automatically.",
      moves: {
        payg: "Still $0 a month. 16% instead of 25%, and 150 GB instead of 25 GB.",
        lite: "Lite stays, and is now free: no more $16.99 a month, 16% instead of 18.5%, and more storage: 150 GB instead of 100 GB. If you need more, extra storage is from $5 per 100 GB a month. Your galleries keep your logo and colours, with a small “Powered by Photohawk” badge.",
        pro: "$29 instead of $29.99 a month, 14% instead of 16%, and 1 TB instead of 500 GB.",
        enterprise: "Enterprise is renamed Max — same plan, new name, now with 3 TB and a 12% rate. It's $49 a month instead of $44.99 from your first renewal on or after 1 November.",
      },
      points: [
        {
          id: "action",
          title: "No action needed",
          body: "Your workspace moves on 1 November 2026. Galleries, prices and payouts carry on as normal.",
        },
        {
          id: "month",
          title: "The full-month rule",
          body: "Move to a plan with a lower rate and that rate applies from your first full month on it. Downgrades take effect at the end of the month.",
        },
        {
          id: "change",
          title: "Changing plan",
          body: "You can change plan any time from Billing in your workspace.",
        },
        {
          id: "deals",
          title: "Custom deals",
          body: "Existing custom deals carry over until they end.",
        },
        {
          id: "privacy",
          title: "Selfie search and privacy",
          body: "From 1 November, you choose how long the face data used for selfie search is kept: No expiry by default, or a period after each photo's upload. Your photos, sales and bib search aren't affected.",
        },
        {
          id: "team",
          title: "Your team",
          body: "Lite includes up to 3 people, Pro up to 10 and Max unlimited. Already have more people in your workspace? They keep their access — the limit only applies when adding someone new.",
        },
      ],
    },
    ftp: {
      /** Shown as a date badge. */
      date: "1 November 2026",
      heading: "The web uploader is now the standard way to upload",
      body: "Always-on shared FTP servers close on 1 November 2026. The web uploader is faster than FTP and makes full use of your connection, on every plan. If FTP is essential, you can start your own server on demand.",
      items: [
        {
          id: "web",
          title: "The web uploader, on every plan",
          body: "It sends many photos at once, far more than an FTP app like FileZilla (2 at a time by default, and no more than 10), and keeps adjusting to make full use of your bandwidth, and if the connection drops it resumes by itself. On every plan, light previews can go up first, so your gallery is live in minutes.",
        },
        {
          id: "ondemand",
          title: "If FTP is essential: on every plan",
          body: "Available now, on every plan: start your own on-demand FTP server from the web uploader, at a personal Photohawk address that never changes. It switches off automatically after 1 hour without activity.",
        },
        {
          id: "studio",
          title: "Photohawk Studio, on every plan",
          body: "The Photohawk uploader app for your workspace, on desktop and iOS.",
        },
      ],
    },
    /** Link under the "privacy" point; shown only once BIOMETRIC_POLICY_PATH (plans.ts) is set. */
    privacyLink: "Biometric data retention policy",
    /** "What's included": a short summary, linking to the full table on the pricing page. */
    included: {
      heading: "What's included on each plan",
      everyPlan:
        "Every plan: selfie search, bib and time search, preview protection, the web uploader with previews first, and your own on-demand FTP server.",
      upgrades: "Pro and Max add visitor analytics and a custom domain. Max adds video upload.",
    },
    compare: {
      heading: "Today vs from 1 November",
      subtitle: "Plan by plan, in US dollars.",
      rows: {
        fee: "Monthly fee",
        commission: "Commission on sales",
        storage: "Storage included",
        downloads: "Free-download photos",
        extraStorage: "Extra storage",
      },
      /** Today's charge on every free-download photo, e.g. "2¢ each". */
      downloadsToday: "{price} each",
      downloadsNext: "{count} a month included, then $8 per 1,000",
      extraStorageToday: "$0.023 per GB a month",
      extraStorageNext: "Packs: +100 GB $5, +1 TB $25, +5 TB $100 a month",
    },
    countries: {
      heading: "What it means where you sell",
      subtitle: "Examples in your currency. Pick a country.",
      selectLabel: "Country",
      examplesBadge: "Examples",
      orderHeading: "A typical order: {amount}",
      orderSubtitle: "The average order {inCountry}, paid by card. Card fee {fee}, {kind}.",
      kind: {
        stripe: "charged by Stripe to your account",
        managed: "deducted where Photohawk processes the payment",
      },
      colPlan: "Your plan",
      colCommission: "Photohawk commission",
      colKeep: "You keep",
      perOrderMore: "+{amount} an order",
      monthlyHeading: "A month of sales, all in",
      monthlySubtitle:
        "Plan fee, commission and card fees together, as a share of your sales, on whichever plan costs least at each level.",
      level: "{amount} a month in sales",
      onPlan: "on {plan}",
      keepMore: "You keep about {amount} more a month",
      disclaimer:
        "Examples only, based on average order values and converted at fixed exchange rates (October 2026). Your figures depend on your orders, cards and plan. Free-download and storage charges aren't included.",
    },
    faq: {
      heading: "Questions",
      items: [
        {
          q: "Do I need to do anything?",
          a: "No. On 1 November 2026 your workspace moves to its new plan automatically. Your galleries, prices and payouts carry on as normal.",
        },
        {
          q: "I'm on Lite. What happens?",
          a: "Lite stays, and is now free: from 1 November there's no more $16.99 a month, your rate is 16% instead of 18.5%, and you get more storage: 150 GB instead of 100 GB. If you store more, extra storage is from $5 per 100 GB a month, added automatically when you have a card on file (without one, new uploads pause until you add a card; nothing is deleted). Your galleries keep your logo and colours, with a small “Powered by Photohawk” badge.",
        },
        {
          q: "What if I store more than my plan includes?",
          a: "With a card on file, storage packs are added automatically, from $5 per 100 GB a month, billed as the cheapest mix that covers what you store. Without a card on file, new uploads pause until you add one: nothing is deleted and your galleries keep selling.",
        },
        {
          q: "I'm on Enterprise. What happens?",
          a: "Enterprise is renamed Max: same plan, new name. From 1 November your rate is 12% instead of 13.5% and you get 3 TB instead of 1 TB. The new price, $49 a month instead of $44.99, applies from your first renewal on or after 1 November.",
        },
        {
          q: "How many people can be in my workspace?",
          a: "Up to 3 people on Lite, up to 10 on Pro and as many as you like on Max. Already have more people in your workspace? They keep their access: the limit only applies when you invite someone new.",
        },
        {
          id: "ftp",
          q: "What's happening to FTP?",
          a: "Always-on shared FTP servers close on 1 November 2026, and the web uploader is now the standard way to upload, on every plan. If FTP is essential, everyone in your workspace, on every plan, can already start their own on-demand FTP server from the web uploader, at a personal Photohawk address that never changes; it switches off automatically after 1 hour without activity.",
        },
        {
          q: "Will my custom deal change?",
          a: "No. Existing custom deals carry over until they end.",
        },
        {
          q: "If I change plan, when does the new rate start?",
          a: "A plan's lower rate applies from your first full month on it. Downgrades take effect at the end of the month.",
        },
        {
          q: "Are card fees changing?",
          a: "No. Card and payment processing fees work just as they do today, separately from our commission.",
        },
        {
          q: "Is Photohawk Friends & Family part of a plan?",
          a: "No. Friends & Family, our app for your family's photos, is a separate app, coming soon, and isn't part of any Studio plan. Running your galleries and uploads? That's Photohawk Studio: it works in your web browser on every plan today, and the Studio app for Mac, iPhone and iPad launches in November 2026.",
        },
      ],
    },
    cta: {
      heading: "See the new pricing in full",
      body: "Plans, storage, free downloads and card fees by country, all on one page.",
      button: "See the new pricing",
    },
  },

  app: {
    badge: "Coming soon",
    platform: "iPhone first",
    heading: "Photohawk Friends & Family, for your family's photos",
    intro: "Friends & Family is our app for all your family's photos: the ones you take and the ones you share. It's a separate app, coming soon, and isn't part of Studio plans.",
    points: [
      { id: "private", title: "Find your family's photos, privately", body: "Sort and find photos of the people you love, right on your phone." },
      { id: "albums", title: "Albums where everyone sees their own photos", body: "Share an album and each person sees just the photos they're in, matched by selfie: the same technology as Photohawk galleries." },
      { id: "event", title: "Collect everyone's photos from an event", body: "Bring together the photos friends and family took, all in one place." },
      { id: "prints", title: "Prints and photo books", body: "Turn the best ones into prints and photo books." },
    ],
    /** No waitlist endpoint exists yet, so the CTA is disabled. Wire it up when there is one. */
    cta: "Notify me",
    ctaNote: "The waitlist opens soon.",
    phoneAlbum: "Just me",
    phoneAlbumCount: "12 photos",
  },
};

export type PricingCopy = typeof en;
export default en;
